/**
 * uploadResilience — shared transport layer for image uploads on flaky networks.
 *
 * Why: multi-MB originals uploaded over cellular stall mid-body and die at nginx
 * before Node ever sees them (zero server logs). The client used to show the
 * picked images anyway — users could pay for photos the server never received.
 *
 * The engine (owner-tuned for INSTANT recovery):
 *  - Two-phase dead-socket detection with an EXACT deadline timer (no polling):
 *    every progress byte re-arms the timer. Before the first byte (DNS/TLS on
 *    weak cellular is slow-but-alive) we allow 8s of silence; once bytes have
 *    flowed, silence means a dead socket (e.g. a 4G→WiFi switch, which fires NO
 *    event on iOS) and we abort after just 4s. A moving upload is never killed.
 *  - NO blind backoff. Failed jobs wait on a CONNECTIVITY GATE: while anything
 *    is stuck, a ~200-byte GET /api/ping probe fires every 2s; the FIRST green
 *    probe re-fires every waiting upload instantly. Good internet returning is
 *    detected within ~2s no matter when it arrives. Probes run only while jobs
 *    are waiting — zero idle traffic.
 *  - Wake signals (online / visibilitychange / connection.change) abort running
 *    attempts that stopped moving (>3s) and trigger an immediate probe.
 *  - Server-rejection damping: if the server RESPONDED with an error (probe is
 *    green but the upload itself is refused), consecutive retries are spaced
 *    5/10/20/30s so we don't hammer a rejecting endpoint at probe cadence.
 *  - NO terminal failure state (owner's call — no retry UI): a job retries until
 *    success or DROP_AFTER_MS total, then the frame is dropped (onDrop); a
 *    refresh drops unconfirmed frames too (gallery rehydrates from the server).
 *  - UI contract: a picked image renders as a plain white frame + gray spinner
 *    and becomes the photo ONLY when the server confirms it received it.
 *  - A fire-and-forget failure beacon to /api/upload-failure so lost uploads
 *    become a measurable number instead of an anecdote.
 *
 *  - prepareUploadFile(file): JPEG re-encode at FULL original resolution —
 *    never downscales (print quality contract). Currently unused in the app
 *    (both uploaders use utils/uploadDownscale's shared 2000px cap); kept for
 *    standalone use.
 */
import axios from "axios";
import { setRecoil } from "recoil-nexus";
import { imagesBlobState } from "../../Recoil/Atoms";
import "./uploadStates.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Upload accelerator: CloudFront edge (nearest POP, e.g. Tel Aviv) -> AWS
// backbone -> origin. A weak cellular last-mile only has to reach the edge
// instead of holding a socket to Zurich. Attempts ALTERNATE accelerated/direct
// (even attempts = edge, odd = direct) so either path being broken can never
// block an upload. Keyed by host so prod stays direct until it gets its own
// distribution.
const UPLOAD_ACCEL_BY_HOST = {
  "test.blends.co.il": "https://d346rmr6onjjpx.cloudfront.net/api",
  "blends.co.il": "https://d13049faagtrr8.cloudfront.net/api",
  "www.blends.co.il": "https://d13049faagtrr8.cloudfront.net/api",
};

/** Base API URL for a given attempt number (0-based). */
export function uploadBaseForAttempt(attemptNo) {
  const accel =
    typeof window !== "undefined" && UPLOAD_ACCEL_BY_HOST[window.location.host];
  if (!accel) return BASE_URL;
  return attemptNo % 2 === 0 ? accel : BASE_URL;
}

// PRINT QUALITY CONTRACT (owner requirement): NO downscaling — ever. We only
// RE-ENCODE at the original resolution (JPEG q0.90, visually lossless).
const JPEG_Q = 0.9;
const MIN_PNG_BYTES = 900 * 1024;
const MIN_OTHER_BYTES = 2500 * 1024;

const CONNECT_STALL_MS = 8000; // silence allowed before the FIRST byte (handshake)
const TRANSFER_STALL_MS = 4000; // silence allowed once bytes were flowing
// Once the whole body is sent, silence is the SERVER working (HEIC decode,
// smartcrop, S3), not a dead socket. Matches nginx's 60s proxy_read_timeout.
// This used to be 4s as well: on 2026-11-03 every slow HEIC reply was aborted
// and re-sent, 12 photos were decoded 170x and the prod box ran out of memory.
const RESPONSE_WAIT_MS = 60000;
const WAKE_STALL_MS = 3000; // on a wake signal, kill attempts quieter than this
const PROBE_INTERVAL_MS = 2000;
const PROBE_TIMEOUT_MS = 2500;
const CONCURRENCY = 2;
const RESP_BACKOFF_S = [5, 10, 20, 30]; // spacing after SERVER-rejected attempts only
// Total time a job may keep spinning before the frame is DROPPED.
const DROP_AFTER_MS = 10 * 60 * 1000;

function loadImg(url) {
  return new Promise((resolve) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = url;
  });
}

/**
 * Re-encode a picked file for transfer at FULL original resolution. Returns
 * { file } — possibly the original untouched. Never throws.
 */
export async function prepareUploadFile(file) {
  try {
    if (!file || !file.size) return { file };
    const isPng = /png/i.test(file.type || "") || /\.png$/i.test(file.name || "");
    if (file.size < (isPng ? MIN_PNG_BYTES : MIN_OTHER_BYTES)) return { file };
    const url = URL.createObjectURL(file);
    const im = await loadImg(url);
    if (!im || !im.naturalWidth) {
      URL.revokeObjectURL(url);
      return { file }; // HEIC on non-Safari etc. — server converts
    }
    const canvas = document.createElement("canvas");
    canvas.width = im.naturalWidth;
    canvas.height = im.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(im, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", JPEG_Q));
    if (!blob || blob.size >= file.size) return { file };
    const base = (file.name || "photo").replace(/\.(heic|heif|png|webp|jpe?g)$/i, "");
    return {
      file: new File([blob], `${base || "photo"}.jpg`, { type: "image/jpeg", lastModified: Date.now() }),
    };
  } catch (e) {
    return { file };
  }
}

// ---------------------------------------------------------------------------
// Resilient upload queue
// ---------------------------------------------------------------------------

const jobs = new Map(); // id -> job
let running = 0;
const slotWaiters = [];

function acquireSlot() {
  if (running < CONCURRENCY) {
    running++;
    return Promise.resolve();
  }
  return new Promise((resolve) => slotWaiters.push(resolve)).then(() => {
    running++;
  });
}

function releaseSlot() {
  running--;
  const next = slotWaiters.shift();
  if (next) next();
}

// --- connectivity gate: probe-driven, instant on recovery -------------------

const gateWaiters = [];
let probeLooping = false;
let probeKick = null; // resolver that short-circuits the inter-probe wait

async function probeOnce() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
    const res = await fetch(`${BASE_URL}/ping?t=${Date.now()}`, {
      method: "GET",
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(t);
    return !!(res && res.ok);
  } catch (e) {
    return false;
  }
}

function startProbeLoop() {
  if (probeLooping) return;
  probeLooping = true;
  (async () => {
    while (gateWaiters.length) {
      const ok = await probeOnce();
      if (ok) {
        // First green probe -> release EVERY waiting upload instantly.
        gateWaiters.splice(0).forEach((resolve) => resolve());
        break;
      }
      await new Promise((resolve) => {
        probeKick = resolve;
        setTimeout(resolve, PROBE_INTERVAL_MS);
      });
      probeKick = null;
    }
    probeLooping = false;
    if (gateWaiters.length) startProbeLoop(); // waiters added while winding down
  })();
}

/** Resolves as soon as a connectivity probe succeeds (~2s granularity). */
function waitForConnectivity() {
  return new Promise((resolve) => {
    gateWaiters.push(resolve);
    startProbeLoop();
  });
}

// Wake sleeps (used only for response-error damping).
function sleepWithWake(job, ms) {
  return new Promise((resolve) => {
    const t = setTimeout(() => {
      job.wake = null;
      resolve();
    }, ms);
    job.wake = () => {
      clearTimeout(t);
      job.wake = null;
      resolve();
    };
  });
}

function onWakeSignal() {
  // Kill running attempts that stopped moving — they're riding a dead socket.
  jobs.forEach((job) => {
    if (job.state === "running" && job.abortIfStalled) job.abortIfStalled();
    if (job.wake) job.wake();
  });
  // Probe NOW instead of waiting out the interval.
  if (probeKick) probeKick();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", onWakeSignal);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") onWakeSignal();
  });
  const conn = navigator.connection;
  if (conn && conn.addEventListener) conn.addEventListener("change", onWakeSignal);
}

export function reportUploadFailure(payload) {
  try {
    axios.post(`${BASE_URL}/upload-failure`, payload, { timeout: 5000 }).catch(() => {});
  } catch (e) {
    /* never break the flow */
  }
}

function beacon(job, cls, err) {
  const conn = typeof navigator !== "undefined" ? navigator.connection : null;
  reportUploadFailure({
    ...(job.meta || {}),
    class: cls,
    message:
      (err && err.response && `HTTP ${err.response.status}`) ||
      (err && err.message) ||
      "unknown",
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    effectiveType: (conn && conn.effectiveType) || null,
    attempts: job.fails,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
  });
}

async function processJob(job) {
  for (;;) {
    // Out of time: drop the job — the caller removes its frame from the UI.
    if (Date.now() - job.startedAt > DROP_AFTER_MS) {
      job.state = "dropped";
      beacon(job, "dropped", null);
      try {
        if (job.onDrop) job.onDrop();
      } catch (e) {
        /* ignore */
      }
      return;
    }

    // Server-rejection damping only: the network is fine (probe was green) but
    // the server refused — space consecutive retries out. Network-class
    // failures never wait here; the probe gate is their only pacing.
    if (job.respFails > 0 && job.lastEndAt) {
      const wait = RESP_BACKOFF_S[Math.min(job.respFails - 1, RESP_BACKOFF_S.length - 1)] * 1000;
      const since = Date.now() - job.lastEndAt;
      if (since < wait) {
        job.state = "waiting";
        await sleepWithWake(job, wait - since);
      }
    }

    await acquireSlot();
    let stallTimer = null;
    try {
      job.state = "running";
      const controller = new AbortController();
      let lastProgress = Date.now();
      job.stalled = false;
      // EXACT deadline, two-phase: 8s of silence allowed before the first byte
      // (handshake), 4s once bytes were flowing. Every byte re-arms the timer.
      const arm = (ms) => {
        clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          job.stalled = true;
          controller.abort();
        }, ms);
      };
      job.abortIfStalled = () => {
        if (Date.now() - lastProgress > WAKE_STALL_MS) {
          job.stalled = true;
          controller.abort();
        }
      };
      arm(CONNECT_STALL_MS);
      const result = await job.attempt({
        signal: controller.signal,
        onUploadProgress: (e) => {
          lastProgress = Date.now();
          const bodySent = e && e.total > 0 && e.loaded >= e.total;
          arm(bodySent ? RESPONSE_WAIT_MS : TRANSFER_STALL_MS);
        },
        attemptNo: job.fails, // lets callers alternate accelerated/direct endpoints
      });
      clearTimeout(stallTimer);
      job.abortIfStalled = null;
      releaseSlot();
      job.state = "done";
      try {
        if (job.onDone) await job.onDone(result);
      } catch (e) {
        /* onDone must not resurrect the job */
      }
      return;
    } catch (err) {
      clearTimeout(stallTimer);
      job.abortIfStalled = null;
      releaseSlot();
      job.lastEndAt = Date.now();
      job.fails++;
      const isResponse = !job.stalled && err && err.response;
      const cls = job.stalled
        ? "stall"
        : isResponse
        ? err.response.status >= 500
          ? "http5xx"
          : "http4xx"
        : "network";
      job.respFails = isResponse ? job.respFails + 1 : 0;
      job.stalled = false;
      if (job.fails === 1) beacon(job, cls, err);
      // Wait for a green probe — retries fire the INSTANT connectivity returns.
      job.state = "waiting";
      await waitForConnectivity();
    }
  }
}

/**
 * Enqueue an upload. `attempt({signal, onUploadProgress})` must return the axios
 * promise. Failures retry the moment a connectivity probe succeeds (server
 * rejections are damped 5-30s) for up to DROP_AFTER_MS total, then the job is
 * dropped and `onDrop` fires (the caller removes the frame). Success calls
 * `onDone` with the response.
 */
export function enqueueResilientUpload({ id, attempt, onDone, onDrop, meta }) {
  const job = {
    id,
    attempt,
    onDone,
    onDrop,
    meta: meta || {},
    state: "queued",
    fails: 0,
    respFails: 0,
    lastEndAt: 0,
    wake: null,
    stalled: false,
    abortIfStalled: null,
    startedAt: Date.now(),
  };
  jobs.set(id, job);
  processJob(job);
  return job;
}

/** Remove a review-gallery blob entry entirely (used when a job is dropped). */
export function removeBlobEntry(id) {
  setRecoil(imagesBlobState, (prev) => (prev || []).filter((b) => b.id !== id));
}

/** True while any upload job has not completed. */
export function hasUnfinishedUploads() {
  let unfinished = false;
  jobs.forEach((job) => {
    if (job.state !== "done") unfinished = true;
  });
  return unfinished;
}
